import React from 'react';

// --- ICONS (Heroicons SVGs) ---
// We only need the close button for this component
const CloseButtonIcon = () => (
  <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

/**
 * A reusable modal dialog component.
 *
 * @param {object} props
 * @param {boolean} props.isOpen - Controls if the dialog is visible.
 * @param {Function} props.onClose - Called when the close button or backdrop is clicked.
 * @param {string} props.title - The main title text.
 * @param {React.ReactNode} props.children - The "explain text" or main content.
 * @param {string} [props.acceptText] - Text for the primary (accept) button.
 * @param {Function} props.onAccept - Function to call when the accept button is clicked.
 * @param {string} [props.rejectText] - Text for the secondary (reject) button.
 * @param {Function} [props.onReject] - Function to call when the reject button is clicked (defaults to onClose).
 * @param {React.ReactNode} [props.footerText] - Small text to display at the bottom.
 * @param {string} [props.className] - Additional classes for the dialog panel.
 */
export default function Dialog({
  title,
  children,
  onAccept,
  onClose,
  acceptText = "Accept",
  rejectText = "Reject",
  footerText = "",
  onReject,
  className = ''
}) {

  // Handle the reject click. Use onReject if provided, otherwise default to onClose.
  const handleReject = onReject || onClose;

  return (
    <>
      {/* 1. Backdrop */}
      {/* Covers the entire screen with a semi-transparent black overlay */}
      <div
        className="fixed inset-0 z-40 bg-black bg-opacity-50 transition-opacity"
        aria-hidden="true"
        onClick={onClose} // Close dialog when backdrop is clicked
      ></div>

      {/* 2. Dialog Wrapper */}
      {/* This centers the dialog panel on the screen */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        
        {/* 3. Dialog Panel */}
        {/* The main white card, styled like the image */}
        <div
          className={`relative w-full max-w-md rounded-2xl bg-white p-6 shadow-xl ${className}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="dialog-title"
        >
          {/* Close Button (Top-Right) */}
          {onClose && (
            <div className="absolute top-4 right-4">
              <button
                onClick={onClose}
                type="button"
                className="p-1 rounded-full text-gray-400 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-400"
                aria-label="Close dialog"
              >
                <CloseButtonIcon />
              </button>
            </div>
          )}

          {/* Title */}
          <h3 id="dialog-title" className="text-center text-2xl font-bold text-gray-900">
            {title}
          </h3>

          {/* Explain Text (Children) */}
          <div className="mt-2 text-center text-sm text-gray-600">
            {children}
          </div>

          {/* Buttons */}
          <div className="mt-6 space-y-3">
            {/* Accept Button (Primary) */}
            <button
              onClick={onAccept}
              type="button"
              className="w-full inline-flex justify-center rounded-lg border border-transparent bg-blue-600 px-4 py-3 text-base font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {acceptText}
            </button>
            
            {/* Reject Button (Secondary) */}
            <button
              onClick={handleReject}
              type="button"
              className="w-full inline-flex justify-center rounded-lg border border-gray-300 bg-white px-4 py-3 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
            >
              {rejectText}
            </button>
          </div>

          {/* Footer Text */}
          {footerText && (
            <div className="mt-4 text-center text-xs text-gray-500">
              {footerText}
            </div>
          )}
        </div>
      </div>
    </>
  );
}